import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, XIcon, SmartphoneIcon } from 'lucide-react';
import { usePwaInstall } from '../../app/hooks/usePwaInstall';
import { DjayNarrator } from '../../mascot';
/**
 * InstallAppPrompt — Vriendelijke, dismissible banner die verschijnt wanneer
 * Chrome/Edge de site als PWA wil installeren.
 *
 * - Verschijnt rechtsonder op desktop, en als bottom-sheet op mobile.
 * - Toont Djay met een vriendelijke uitnodiging.
 * - Gebruiker kan installeren (native prompt) of wegklikken (14 dagen stil).
 * - Werkt alleen op de gepubliceerde HTTPS-site — niet in de sandbox preview.
 */
export function InstallAppPrompt() {
  const {
    canInstall,
    promptInstall,
    dismiss
  } = usePwaInstall();
  return <AnimatePresence data-id="element-2436" data-mp="1foy1etb7ysn4">
      {canInstall && <motion.div key="install-prompt" initial={{
      y: 40,
      opacity: 0,
      scale: 0.96
    }} animate={{
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      y: 40,
      opacity: 0,
      scale: 0.96
    }} transition={{
      duration: 0.3,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="fixed z-[55] bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-sm" role="dialog" aria-labelledby="pwa-install-title" aria-describedby="pwa-install-desc" data-id="element-2437" data-mp="1kmrb3wbqe4s5">
          <div className="relative bg-white border border-orange-100 rounded-2xl shadow-xl p-4 md:p-5 overflow-hidden" data-id="element-2438" data-mp="3s6s3ijkmmcpl">
            {/* Dismiss knop */}
            <button type="button" onClick={dismiss} aria-label="Sluit installatie-melding" className="absolute top-2 right-2 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300" data-id="element-2439" data-mp="3nes54vk7frv4">
              <XIcon className="w-4 h-4" aria-hidden="true" data-id="element-2440" data-mp="298vvh54w7xze" />
            </button>

            <div className="flex items-start gap-3 pr-6" data-id="element-2441" data-mp="2e7qm2t93qqu2">
              {/* Djay */}
              <div className="flex-shrink-0" data-id="element-2442" data-mp="2e9td9glzqq0d">
                <DjayNarrator size="sm" emotion="excited" data-id="element-2443" data-mp="1zjm0nhio017l" />
              </div>

              {/* Tekst */}
              <div className="flex-1 min-w-0 pt-1" data-id="element-2444" data-mp="24gvo4rmhu2ss">
                <h3 id="pwa-install-title" className="text-sm font-extrabold text-slate-900 leading-tight" data-id="element-2445" data-mp="24fkn2zkfn95d">
                  Installeer Meester Djay
                </h3>
                <p id="pwa-install-desc" className="mt-1 text-xs text-slate-600 leading-snug" data-id="element-2446" data-mp="1gkhssc2v0dhi">
                  Zet me als app op je startscherm — sneller starten, geen
                  browser-balk, en altijd dichtbij.
                </p>
              </div>
            </div>

            {/* Knoppen */}
            <div className="mt-4 flex items-center gap-2" data-id="element-2447" data-mp="3fuzpwjzbov8e">
              <button type="button" onClick={() => void promptInstall()} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2" data-id="element-2448" data-mp="14as6r0ycwx75">
                <DownloadIcon className="w-4 h-4" aria-hidden="true" data-id="element-2449" data-mp="1mzf7x8dex2fg" />
                Installeer app
              </button>
              <button type="button" onClick={dismiss} className="px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300" data-id="element-2450" data-mp="1i0lvlj9v3hgv">
                Later
              </button>
            </div>

            {/* Subtiele hint icoon strip */}
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400" data-id="element-2451" data-mp="3trzldypckdj8">
              <SmartphoneIcon className="w-3 h-3" aria-hidden="true" data-id="element-2452" data-mp="2ksoe9ioqvz6h" />
              <span data-id="element-2453" data-mp="2pskjtmd3byo5">Werkt op telefoon, tablet en computer</span>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}